import { useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';

function useBookingData(therapistId) {
  const [schedules, setSchedules] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [services, setServices] = useState([]);
  const [newBookingIds, setNewBookingIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: schedData, error: schedError } = await supabase
        .from('availability_schedules')
        .select('*')
        .eq('therapist_id', therapistId)
        .order('day_of_week');
      if (schedError) throw schedError;
      setSchedules(schedData || []);

      const { data: bookData, error: bookError } = await supabase
        .from('bookings')
        .select('*, client:clients(*), service:services(*)')
        .eq('therapist_id', therapistId)
        .eq('status', 'confirmed')
        .order('date')
        .order('start_time');
      if (bookError) throw bookError;

      // Fånga vilka IDs som är nya (osedda) innan vi markerar dem som sedda
      const unseenIds = new Set(
        (bookData || []).filter(b => !b.therapist_seen).map(b => b.id)
      );
      setNewBookingIds(unseenIds);
      setBookings(bookData || []);

      // Markera klientbokningar som sedda av terapeut (inkl. avbokade)
      await supabase
        .from('bookings')
        .update({ therapist_seen: true })
        .eq('therapist_id', therapistId)
        .eq('therapist_seen', false);

      const { data: blockedData, error: blockedError } = await supabase
        .from('blocked_times')
        .select('*')
        .eq('therapist_id', therapistId)
        .order('date');
      if (blockedError) throw blockedError;
      setBlockedTimes(blockedData || []);

      const { data: servicesData, error: servicesError } = await supabase
        .from('services')
        .select('*')
        .eq('therapist_id', therapistId)
        .eq('is_deleted', false)
        .order('created_at');
      if (servicesError) throw servicesError;
      setServices(servicesData || []);
    } catch (err) {
      setError(err.message || 'Något gick fel vid hämtning av bokningsdata.');
    }

    setLoading(false);
  }, [therapistId]);

  return { schedules, bookings, blockedTimes, services, newBookingIds, loading, error, refetch };
}

export default useBookingData;
